import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { BookOpen, CheckSquare, Trophy, Home, Star, Zap, ArrowLeft, Award, PenLine, ChevronDown, ChevronUp, Heart, Lightbulb, Send, Loader, Gift, Sparkles, LogOut, BookMarked, Play, Check, Video, Calendar, Brain } from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { MascotBubble, MascotImage, MASCOTS } from "./Mascots";
import { useLanguage } from "../hooks/useLanguage";
import StreakTracker from "./StreakTracker";
import { CertificateChecker } from "./WeeklyCertificate";
import MemoryVerseGame from "./MemoryVerseGame";

export const BottomNav = ({ childId, active }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const tabs = [
    { id: "home",   icon: Home,        label: t('home'),    path: `/child/${childId}`,         color: "#118AB2" },
    { id: "lesson", icon: BookOpen,    label: t('lesson'),  path: `/child/${childId}/lesson`,  color: "#06D6A0" },
    { id: "tasks",  icon: CheckSquare, label: t('tasks'),   path: `/child/${childId}/tasks`,   color: "#9B5DE5" },
    { id: "rewards",icon: Trophy,      label: t('rewards'), path: `/child/${childId}/rewards`, color: "#EF476F" },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.12)]">
      <div className="flex justify-around items-center py-1 max-w-4xl mx-auto pr-16 sm:pr-0">
        {tabs.map(({ id, icon: Icon, label, path, color }) => (
          <button key={id} onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-2xl transition-all"
            style={active === id ? { background: color + "22" } : {}}
            data-testid={`nav-${id}`}>
            <Icon size={22} strokeWidth={active === id ? 2.5 : 2}
              color={active === id ? color : "#CBD5E1"} />
            <span className="text-xs font-bold" style={{ color: active === id ? color : "#CBD5E1",
              fontFamily: "Quicksand, sans-serif" }}>{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default function ChildDashboard() {
  const { childId } = useParams();
  const navigate    = useNavigate();
  const { logout } = useAuth();
  const { t, language } = useLanguage();
  const [child, setChild]           = useState(null);
  const [progress, setProgress]     = useState(null);
  const [todayLesson, setTodayLesson] = useState(null);
  const [todayTasks, setTodayTasks] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [reflections, setReflections] = useState([]);
  const [parentNotes, setParentNotes] = useState([]);
  const [weeklyPrize, setWeeklyPrize] = useState(null);
  const [memoryVerse, setMemoryVerse] = useState(null);
  const [familyLesson, setFamilyLesson] = useState(null);
  const [showVerseGame, setShowVerseGame] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => { loadData(); }, [childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [childRes, progressRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`),
        axios.get(`${API}/progress/${childId}`),
      ]);
      setChild(childRes.data);
      setProgress(progressRes.data);
      try {
        // Pass user's local date to ensure timezone consistency
        const userDate = new Date().toISOString().split('T')[0];
        const lr = await axios.get(`${API}/lessons/today/${childId}?user_date=${userDate}`);
        setTodayLesson(lr.data);
        try { const tr = await axios.get(`${API}/tasks/${childId}?user_date=${userDate}`); setTodayTasks(tr.data); } catch {}
        // Load reflections for older kids
        if (childRes.data?.age_group !== "little") {
          try {
            const refRes = await axios.get(`${API}/reflections/child/${childId}`);
            setReflections(refRes.data);
          } catch {}
        }
      } catch {}
      // Load parent notes for this child
      try {
        const notesRes = await axios.get(`${API}/parent-notes/child/${childId}`);
        setParentNotes(notesRes.data || []);
      } catch {}
      // Load weekly prize
      try {
        const prizeRes = await axios.get(`${API}/children/${childId}/weekly-prize`);
        setWeeklyPrize(prizeRes.data);
      } catch {}
      // Load memory verse progress
      try {
        const verseRes = await axios.get(`${API}/memory-verse/weekly/${childId}`);
        setMemoryVerse(verseRes.data);
      } catch {}
      // Load family lesson day info
      try {
        const familyRes = await axios.get(`${API}/family/weekly-lesson`);
        setFamilyLesson(familyRes.data);
      } catch {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
      <div className="text-center">
        <BookOpen size={64} color="#118AB2" className="mx-auto kid-bounce mb-4" />
        <p className="font-black text-xl text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {t('loading')}
        </p>
      </div>
    </div>
  );

  if (!child) return <div className="min-h-screen flex items-center justify-center"><p>Child not found</p></div>;

  const tasksCompleted = todayTasks?.tasks?.filter(t => t.completed).length || 0;
  const totalTasks     = todayTasks?.tasks?.length || 3;
  const lessonDone     = todayLesson?.completed || false;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('good_morning') : hour < 17 ? t('good_afternoon') : t('good_evening');
  const ageGroupLabel = child.age_group === "little" ? t('little_light') : child.age_group === "big" ? t('bible_explorer') : t('faith_builder');

  return (
    <div className="min-h-screen pb-24" style={{ background: "#F0F4FF" }}>
      {/* Rainbow gradient header */}
      <div className="px-4 pt-6 pb-12 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #118AB2 0%, #06D6A0 50%, #FFD166 100%)" }}>
        {/* Floating mascot decorations */}
        <div className="absolute top-4 right-4 opacity-40 animate-bounce" style={{ animationDuration: "3s" }}>
          <MascotImage mascot="butterfly" size={48} />
        </div>
        <div className="absolute bottom-4 left-2 opacity-30 animate-bounce" style={{ animationDuration: "4s", animationDelay: "1s" }}>
          <MascotImage mascot="bunny" size={44} />
        </div>
        <div className="absolute top-20 left-6 opacity-25 animate-bounce" style={{ animationDuration: "3.5s", animationDelay: "0.5s" }}>
          <MascotImage mascot="fish" size={36} />
        </div>
        <div className="absolute bottom-8 right-8 opacity-30 animate-bounce" style={{ animationDuration: "2.5s", animationDelay: "1.5s" }}>
          <MascotImage mascot="owl" size={42} />
        </div>
        
        <div className="flex items-center justify-between mb-5 max-w-4xl mx-auto relative z-10">
          <button 
            onClick={() => navigate('/parent')}
            className="flex items-center gap-1 text-white font-bold bg-white/20 px-3 py-1.5 rounded-xl hover:bg-white/30 transition-all"
            data-testid="back-to-parent-btn"
          >
            <ArrowLeft size={16} /> {t('back') || 'Back'}
          </button>
          <div className="text-white text-sm font-semibold bg-white/20 px-3 py-1.5 rounded-xl">
            {new Date().toLocaleDateString(language === 'es' ? "es-ES" : "en-US", { weekday: "short", month: "short", day: "numeric" })}
          </div>
        </div>
        <div className="flex items-center gap-4 max-w-4xl mx-auto relative z-10">
          <AvatarDisplay avatar={child.avatar} size={68} />
          <div>
            <p className="text-white/80 font-semibold text-sm">{greeting}!</p>
            <h1 className="text-3xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif",
              textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>{child.name}</h1>
            <p className="text-white/80 text-sm font-semibold">
              {ageGroupLabel} · {t('age')} {child.age}
            </p>
          </div>
        </div>
      </div>

      {/* Stats banner */}
      <div className="max-w-4xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-xl p-4 flex justify-around border-b-4" style={{ borderColor: "#FFD166" }}>
          {[
            { label: t('streak'), value: `${progress?.current_streak||0}d`, color: "#9B5DE5", bg: "#F0E6FF", icon: Zap },
            { label: t('lesson'), value: progress?.total_lessons||0, color: "#118AB2", bg: "#E0F4FF", icon: BookOpen },
            { label: t('stars'),   value: progress?.total_stars||0,   color: "#F77F00", bg: "#FFF3CD", icon: Star },
            { label: t('badges'),  value: progress?.badges?.length||0, color: "#EF476F", bg: "#FFE0EB", icon: Award },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="rounded-xl p-1.5 mx-auto w-9 h-9 flex items-center justify-center mb-1"
                style={{ background: s.bg }}>
                <s.icon size={16} color={s.color} strokeWidth={2.5} />
              </div>
              <p className="font-black text-lg leading-none" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs font-semibold text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Streak Tracker & Certificate */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-2 gap-3">
          <StreakTracker childId={childId} childName={child?.name} />
          <CertificateChecker childId={childId} childName={child?.name} />
        </div>
      </div>

      {/* Main Action Grid - Compact Boxes */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <h2 className="text-lg font-black text-[#073B4C] mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {t('todays_adventure')}
        </h2>
        <div className="grid grid-cols-3 gap-3">
          {/* Lesson */}
          <div onClick={() => navigate(`/child/${childId}/lesson`)}
            className="rounded-2xl p-3 cursor-pointer hover:scale-[1.03] active:scale-95 transition-all border-b-4 shadow-md"
            style={lessonDone
              ? { background: "linear-gradient(135deg, #06D6A0, #118AB2)", borderColor: "#049a73" }
              : { background: "linear-gradient(135deg, #118AB2, #073B4C)", borderColor: "#0a6282" }}
            data-testid="lesson-card">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
              <BookOpen size={20} color="white" strokeWidth={2.5} />
            </div>
            <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {lessonDone ? t('lesson_completed') : t('todays_lesson')}
            </p>
            {lessonDone ? (
              <Award size={16} color="#FFD166" strokeWidth={2.5} className="mt-1" />
            ) : (
              <span className="inline-block bg-white text-[#118AB2] text-[10px] font-black px-2 py-0.5 rounded-full mt-1">{t('start')}</span>
            )}
          </div>

          {/* Tasks */}
          <div onClick={() => navigate(`/child/${childId}/tasks`)}
            className="rounded-2xl p-3 cursor-pointer hover:scale-[1.03] active:scale-95 transition-all border-b-4 shadow-md"
            style={tasksCompleted === totalTasks && totalTasks > 0
              ? { background: "linear-gradient(135deg, #FFD166, #F77F00)", borderColor: "#e0b040" }
              : { background: "linear-gradient(135deg, #9B5DE5, #EF476F)", borderColor: "#7a3fc3" }}
            data-testid="tasks-card">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
              <CheckSquare size={20} color="white" strokeWidth={2.5} />
            </div>
            <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('fun_activities')}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${totalTasks > 0 ? (tasksCompleted / totalTasks) * 100 : 0}%` }} />
              </div>
              <span className="text-white text-[10px] font-black">{tasksCompleted}/{totalTasks}</span>
            </div>
          </div>

          {/* Rewards */}
          <div onClick={() => navigate(`/child/${childId}/rewards`)}
            className="rounded-2xl p-3 cursor-pointer hover:scale-[1.03] active:scale-95 transition-all border-b-4 shadow-md"
            style={{ background: "linear-gradient(135deg, #EF476F, #FFD166)", borderColor: "#c8375a" }}
            data-testid="rewards-card">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
              <Trophy size={20} color="white" strokeWidth={2.5} />
            </div>
            <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('my_rewards')}
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <Star size={12} color="#FFD166" fill="#FFD166" />
              <span className="text-white text-[10px] font-black">{progress?.total_stars||0}</span>
              <Award size={12} color="white" />
              <span className="text-white text-[10px] font-black">{progress?.badges?.length||0}</span>
            </div>
          </div>
        </div>

        {/* Second Row: Verse Game + Family Lesson */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          {/* Verse Practice Game */}
          <div onClick={() => setShowVerseGame(true)}
            className="rounded-2xl p-3 cursor-pointer hover:scale-[1.03] active:scale-95 transition-all border-b-4 shadow-md"
            style={{ background: "linear-gradient(135deg, #9B5DE5, #118AB2)", borderColor: "#7a3fc3" }}
            data-testid="verse-game-card">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
              <Brain size={20} color="white" strokeWidth={2.5} />
            </div>
            <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('verse_practice') || "Verse Practice"}
            </p>
            <p className="text-white/70 text-[10px] font-medium mt-0.5">
              {t('practice_memorize') || "Memorize the verse!"}
            </p>
          </div>

          {/* Family Lesson */}
          {familyLesson ? (
            <a href={familyLesson.video?.youtube_url} target="_blank" rel="noopener noreferrer"
              className="rounded-2xl p-3 hover:scale-[1.03] active:scale-95 transition-all border-b-4 shadow-md block"
              style={{ background: "linear-gradient(135deg, #EF476F, #9B5DE5)", borderColor: "#c8375a" }}
              data-testid="family-lesson-card">
              <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
                <Video size={20} color="white" strokeWidth={2.5} />
              </div>
              <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('family_lesson_day_label') || "Family Lesson"}
              </p>
              <p className="text-white/70 text-[10px] font-medium mt-0.5">
                {familyLesson.family_lesson_day_name}
                {familyLesson.is_lesson_day_today && <span className="ml-1 text-[#FFD166] font-black animate-pulse">TODAY!</span>}
              </p>
            </a>
          ) : (
            <div className="rounded-2xl p-3 border-b-4 shadow-md"
              style={{ background: "linear-gradient(135deg, #E0E0E0, #BDBDBD)", borderColor: "#9E9E9E" }}>
              <div className="bg-white/20 rounded-xl p-2 w-fit mb-2">
                <Video size={20} color="white" strokeWidth={2.5} />
              </div>
              <p className="font-black text-xs text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>Family Lesson</p>
              <p className="text-white/70 text-[10px] font-medium mt-0.5">Coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Compact Info Row: Prize + Verse */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-2 gap-3">
          {weeklyPrize?.prize && (
            <WeeklyPrizeCardCompact weeklyPrize={weeklyPrize} t={t} />
          )}
          {memoryVerse && (
            <MemoryVerseCardCompact memoryVerse={memoryVerse} childId={childId} onUpdate={loadData} t={t} />
          )}
        </div>
      </div>

      {/* Recent badges - Compact pill row */}
      {progress?.badges?.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 mt-3">
          <div className="bg-white rounded-2xl shadow-md p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('recent_badges')}
              </h3>
              <span className="text-[10px] text-gray-400">{progress.badges.length} total</span>
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {progress.badges.slice(0, 5).map((badge, i) => (
                <div key={i} className="flex-shrink-0 flex items-center gap-1 rounded-full px-2 py-0.5"
                  style={{ background: ["#E0F4FF","#FFF3CD","#E0FFF5","#FFE0EB","#F0E6FF"][i%5] }}>
                  <Award size={12} color={["#118AB2","#F77F00","#06D6A0","#EF476F","#9B5DE5"][i%5]} strokeWidth={2.5} />
                  <span className="text-[10px] font-bold text-[#073B4C]">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Parent Notes Section */}
      {parentNotes.length > 0 && (
        <ParentNotesSection parentNotes={parentNotes} t={t} />
      )}

      {/* My Journal Section - Only for kids 8+ */}
      {(child?.age_group === "big" || child?.age_group === "teen") && (
        <JournalSection 
          childId={childId} 
          childName={child?.name}
          reflections={reflections} 
          todayLesson={todayLesson}
          onNewEntry={loadData}
          t={t}
        />
      )}

      {/* Logout Button */}
      <div className="max-w-4xl mx-auto px-4 mt-6 mb-24">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-gray-100 hover:bg-gray-200 transition-all"
          data-testid="child-logout-btn"
        >
          <LogOut size={18} className="text-gray-500" />
          <span className="font-bold text-gray-500">{t('logout') || 'Log Out'}</span>
        </button>
      </div>

      {/* Verse Practice Game Modal */}
      {showVerseGame && (
        <MemoryVerseGame
          childId={childId}
          childName={child?.name}
          onClose={() => setShowVerseGame(false)}
        />
      )}

      <BottomNav childId={childId} active="home" />
    </div>
  );
}

// Weekly Prize Card Component
function WeeklyPrizeCard({ weeklyPrize, t }) {
  if (!weeklyPrize?.prize) return null;

  const prize = weeklyPrize.prize;
  const stats = weeklyPrize.week_stats || {};
  const earned = prize.earned;
  
  // Calculate progress - need 5 lessons AND 15 tasks
  const lessonsNeeded = 5;
  const tasksNeeded = 15;
  const lessonsCompleted = Math.min(stats.lessons_completed || 0, lessonsNeeded);
  const tasksCompleted = Math.min(stats.tasks_completed || 0, tasksNeeded);
  
  const lessonsProgress = (lessonsCompleted / lessonsNeeded) * 100;
  const tasksProgress = (tasksCompleted / tasksNeeded) * 100;
  const overallProgress = ((lessonsCompleted / lessonsNeeded) + (tasksCompleted / tasksNeeded)) / 2 * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 mt-4">
      <div className={`rounded-3xl overflow-hidden shadow-lg border-b-4 ${
        earned ? 'border-[#FFD166]' : 'border-[#9B5DE5]'
      }`}>
        {/* Header */}
        <div className="p-4 relative overflow-hidden"
          style={{ 
            background: earned 
              ? "linear-gradient(135deg, #FFD166 0%, #F77F00 100%)" 
              : "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)"
          }}>
          {/* Sparkle decorations */}
          <div className="absolute top-2 right-4 opacity-30">
            <Sparkles size={24} color="white" />
          </div>
          <div className="absolute bottom-2 left-8 opacity-20">
            <Sparkles size={16} color="white" />
          </div>
          
          <div className="flex items-center gap-3 relative z-10">
            <div className={`rounded-2xl p-3 ${earned ? 'bg-white/30' : 'bg-white/20'}`}>
              <Gift size={28} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-xs font-bold uppercase tracking-wider">
                {earned ? t('prize_earned') || "Prize Earned!" : t('this_weeks_prize_title') || "This Week's Prize"}
              </p>
              <h3 className="font-black text-xl text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {prize.prize_name}
              </h3>
            </div>
            {earned && (
              <div className="bg-white rounded-full p-2">
                <Trophy size={24} color="#F77F00" fill="#F77F00" />
              </div>
            )}
          </div>
        </div>

        {/* Prize description and progress */}
        <div className="bg-white p-4">
          {prize.prize_description && (
            <p className="text-gray-600 text-sm mb-4" style={{ fontFamily: "Quicksand, sans-serif" }}>
              {prize.prize_description}
            </p>
          )}

          {earned ? (
            <div className="text-center py-3">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFD166] to-[#F77F00] text-white font-black px-6 py-3 rounded-2xl">
                <Trophy size={20} />
                <span style={{ fontFamily: "Fredoka, sans-serif" }}>{t('you_did_it') || 'You Did It!'}</span>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                {t('ask_mom_dad') || 'Ask mom or dad for your prize!'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">{t('your_progress') || 'Your Progress'}</p>
              
              {/* Overall progress bar */}
              <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${overallProgress}%`,
                    background: "linear-gradient(90deg, #9B5DE5, #EF476F)"
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-black text-gray-700">
                    {Math.round(overallProgress)}%
                  </span>
                </div>
              </div>

              {/* Individual progress */}
              <div className="grid grid-cols-2 gap-3">
                {/* Lessons progress */}
                <div className="rounded-2xl p-3" style={{ background: "#E0F4FF" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen size={16} color="#118AB2" strokeWidth={2.5} />
                    <span className="text-xs font-bold text-[#118AB2]">Lessons</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ width: `${lessonsProgress}%`, background: "#118AB2" }}
                    />
                  </div>
                  <p className="text-xs font-black text-[#118AB2] mt-1 text-center">
                    {lessonsCompleted}/{lessonsNeeded}
                  </p>
                </div>

                {/* Tasks progress */}
                <div className="rounded-2xl p-3" style={{ background: "#F0E6FF" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckSquare size={16} color="#9B5DE5" strokeWidth={2.5} />
                    <span className="text-xs font-bold text-[#9B5DE5]">Activities</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ width: `${tasksProgress}%`, background: "#9B5DE5" }}
                    />
                  </div>
                  <p className="text-xs font-black text-[#9B5DE5] mt-1 text-center">
                    {tasksCompleted}/{tasksNeeded}
                  </p>
                </div>
              </div>

              <p className="text-center text-xs text-gray-400 mt-2">
                Complete {lessonsNeeded} lessons and {tasksNeeded} activities to earn your prize!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Memory Verse of the Week Component
function MemoryVerseCard({ memoryVerse, childId, onUpdate, t }) {
  const [practicing, setPracticing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showVerse, setShowVerse] = useState(true);
  const [isReading, setIsReading] = useState(false);

  if (!memoryVerse) return null;

  const readVerseAloud = () => {
    if ('speechSynthesis' in window && memoryVerse?.verse) {
      // Stop any ongoing speech
      window.speechSynthesis.cancel();
      
      const text = `${memoryVerse.verse}. ${memoryVerse.reference}`;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.85; // Slightly slower for kids
      utterance.pitch = 1.1;
      utterance.lang = 'en-US';
      
      // Get available voices and prefer a female/child-friendly voice
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        // Try to find an English voice
        const englishVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
        utterance.voice = englishVoice;
      }
      
      utterance.onstart = () => setIsReading(true);
      utterance.onend = () => setIsReading(false);
      utterance.onerror = (e) => {
        console.error('Speech error:', e);
        setIsReading(false);
      };
      
      // Some browsers need a slight delay for voices to load
      if (voices.length === 0) {
        window.speechSynthesis.onvoiceschanged = () => {
          window.speechSynthesis.speak(utterance);
        };
      } else {
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handlePractice = async () => {
    setPracticing(true);
    // Read verse aloud when practicing
    readVerseAloud();
    try {
      await axios.post(`${API}/memory-verse/weekly/${childId}/practice`);
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setPracticing(false); }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API}/memory-verse/weekly/${childId}/complete`);
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  const isCompleted = memoryVerse.completed;
  const practiceCount = memoryVerse.practice_count || 0;

  return (
    <div className="max-w-4xl mx-auto px-4 mt-4">
      <div className={`rounded-3xl overflow-hidden shadow-lg border-b-4 ${
        isCompleted ? 'border-[#06D6A0]' : 'border-[#118AB2]'
      }`}>
        {/* Header */}
        <div className="p-4 relative overflow-hidden"
          style={{ 
            background: isCompleted 
              ? "linear-gradient(135deg, #06D6A0 0%, #118AB2 100%)" 
              : "linear-gradient(135deg, #118AB2 0%, #073B4C 100%)"
          }}>
          {/* Decorations */}
          <div className="absolute top-2 right-4 opacity-30">
            <BookMarked size={24} color="white" />
          </div>
          
          <div className="flex items-center gap-3 relative z-10">
            <div className={`rounded-2xl p-3 ${isCompleted ? 'bg-white/30' : 'bg-white/20'}`}>
              <BookMarked size={28} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-xs font-bold uppercase tracking-wider">
                {isCompleted ? t('memorized') || "Memorized!" : t('memory_verse') || "Memory Verse"}
              </p>
              <h3 className="font-black text-lg text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('week_number')?.replace('{num}', memoryVerse.week_number) || `Week ${memoryVerse.week_number}`} · {memoryVerse.theme}
              </h3>
            </div>
            {isCompleted && (
              <div className="bg-white rounded-full p-2">
                <Check size={24} color="#06D6A0" strokeWidth={3} />
              </div>
            )}
          </div>
        </div>

        {/* Verse Content */}
        <div className="bg-white p-4">
          {/* The Verse */}
          <div 
            className="rounded-2xl p-4 mb-4 cursor-pointer transition-all"
            style={{ background: isCompleted ? "#E0FFF5" : "#E0F4FF" }}
            onClick={() => setShowVerse(!showVerse)}
          >
            {showVerse ? (
              <>
                <p className="text-lg font-bold text-[#073B4C] leading-relaxed text-center italic mb-2"
                  style={{ fontFamily: "Georgia, serif" }}>
                  "{memoryVerse.verse}"
                </p>
                <p className="text-sm font-black text-center"
                  style={{ color: isCompleted ? "#06D6A0" : "#118AB2" }}>
                  — {memoryVerse.reference}
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500 font-bold">Tap to reveal the verse!</p>
                <p className="text-xs text-gray-400 mt-1">Can you remember it?</p>
              </div>
            )}
          </div>

          {isCompleted ? (
            <div className="text-center py-2">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#06D6A0] to-[#118AB2] text-white font-black px-6 py-3 rounded-2xl">
                <Star size={20} fill="white" />
                <span style={{ fontFamily: "Fredoka, sans-serif" }}>You Memorized It! +5 Stars</span>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                Great job! Keep saying it every day!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Practice counter */}
              <div className="flex items-center justify-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <div 
                      key={n}
                      className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                        n <= practiceCount 
                          ? 'bg-[#118AB2]' 
                          : 'bg-gray-200'
                      }`}
                    >
                      {n <= practiceCount && <Check size={12} color="white" strokeWidth={3} />}
                    </div>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-500">
                  {practiceCount}/10 practices
                </span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                {/* Listen button - just reads the verse */}
                <button
                  onClick={readVerseAloud}
                  disabled={isReading}
                  className="flex items-center justify-center gap-1 bg-[#9B5DE5] hover:bg-[#8347cc] text-white font-black px-4 py-3 rounded-xl transition-all disabled:opacity-50"
                  style={{ fontFamily: "Fredoka, sans-serif" }}
                  data-testid="listen-verse-btn"
                  title="Listen to the verse"
                >
                  {isReading ? <Loader size={18} className="animate-spin" /> : <Play size={18} fill="white" />}
                </button>
                
                <button
                  onClick={handlePractice}
                  disabled={practicing || isReading}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#118AB2] hover:bg-[#0a6d8f] text-white font-black py-3 rounded-xl transition-all disabled:opacity-50"
                  style={{ fontFamily: "Fredoka, sans-serif" }}
                  data-testid="practice-verse-btn"
                >
                  {practicing ? <Loader size={18} className="animate-spin" /> : <Play size={18} />}
                  {t('practice') || "Practice"}
                </button>
                
                {practiceCount >= 3 && (
                  <button
                    onClick={handleComplete}
                    disabled={completing}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#06D6A0] to-[#118AB2] text-white font-black py-3 rounded-xl transition-all disabled:opacity-50"
                    style={{ fontFamily: "Fredoka, sans-serif" }}
                    data-testid="complete-verse-btn"
                  >
                    {completing ? <Loader size={18} className="animate-spin" /> : <Check size={18} />}
                    {t('i_know_it') || "I Know It!"}
                  </button>
                )}
              </div>

              <p className="text-center text-xs text-gray-400">
                {practiceCount < 3 
                  ? (t('practice_to_unlock')?.replace('{count}', 3 - practiceCount) || `Practice ${3 - practiceCount} more time${3 - practiceCount > 1 ? 's' : ''} to unlock completion!`)
                  : (t('ready_to_complete') || "Ready to show you know it? Tap 'I Know It!'")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Journal Section Component for Kids 8+
function JournalSection({ childId, childName, reflections, todayLesson, onNewEntry, t }) {
  const [expanded, setExpanded] = useState(false);
  const [newEntry, setNewEntry] = useState("");
  const [saving, setSaving] = useState(false);

  const todayReflections = reflections.filter(r => {
    const today = new Date().toISOString().split('T')[0];
    return r.created_at?.startsWith(today);
  });

  const pastReflections = reflections.filter(r => {
    const today = new Date().toISOString().split('T')[0];
    return !r.created_at?.startsWith(today);
  }).slice(0, 10);

  const handleSaveEntry = async () => {
    if (!newEntry.trim() || !todayLesson?.id) return;
    setSaving(true);
    try {
      await axios.post(`${API}/reflections`, {
        lesson_id: todayLesson.id,
        child_id: childId,
        prompt_index: null,
        content: newEntry,
        reflection_type: "idea"
      });
      setNewEntry("");
      onNewEntry?.();
    } catch (e) {
      console.error("Failed to save entry:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 mt-6">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
        {/* Header */}
        <div className="p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PenLine size={22} color="white" strokeWidth={2.5} />
              <h3 className="font-black text-white text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('todays_thoughts') || "Today's Thoughts"}
              </h3>
            </div>
            <button 
              onClick={() => setExpanded(!expanded)}
              className="bg-white/20 rounded-xl p-1.5"
            >
              {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
            </button>
          </div>
          <p className="text-white/80 text-xs mt-1">
            {todayReflections.length > 0 
              ? (t('entries_today')?.replace('{count}', todayReflections.length) || `${todayReflections.length} entries today`)
              : (t('write_thoughts') || "Write your thoughts about today's lesson")}
          </p>
        </div>

        {/* Quick Write - Always visible */}
        <div className="p-4 border-b">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "#F0E6FF" }}>
              <Heart size={16} color="#9B5DE5" />
            </div>
            <div className="flex-1">
              <textarea
                value={newEntry}
                onChange={(e) => setNewEntry(e.target.value)}
                className="w-full p-2 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none resize-none text-sm"
                rows={2}
                placeholder={t('whats_on_mind') || "What's on your mind today? Write anything here..."}
                data-testid="quick-journal-input"
              />
              <button
                onClick={handleSaveEntry}
                disabled={saving || !newEntry.trim() || !todayLesson?.id}
                className="mt-2 bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] text-white font-bold py-2 px-4 rounded-xl text-sm disabled:opacity-50 flex items-center gap-2"
                data-testid="save-quick-journal"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                {t('save') || 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Today's Entries */}
        {todayReflections.length > 0 && (
          <div className="p-4 border-b bg-purple-50">
            <p className="text-xs font-bold text-[#9B5DE5] uppercase tracking-wide mb-2">{t('todays_thoughts') || "Today's Thoughts"}</p>
            <div className="space-y-2">
              {todayReflections.map((ref, i) => (
                <div key={i} className="bg-white rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    {ref.reflection_type === "idea" ? (
                      <Heart size={14} color="#EF476F" className="mt-0.5 flex-shrink-0" />
                    ) : (
                      <Lightbulb size={14} color="#9B5DE5" className="mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">"{ref.content}"</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(ref.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Entries - Expanded */}
        {expanded && pastReflections.length > 0 && (
          <div className="p-4 max-h-64 overflow-y-auto">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('past_entries') || "Past Entries"}</p>
            <div className="space-y-2">
              {pastReflections.map((ref, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-sm text-gray-700">"{ref.content}"</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(ref.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expand hint */}
        {!expanded && pastReflections.length > 0 && (
          <button 
            onClick={() => setExpanded(true)}
            className="w-full p-3 text-center text-sm text-[#9B5DE5] font-bold hover:bg-purple-50"
          >
            {t('view_past_entries')?.replace('{count}', pastReflections.length) || `View ${pastReflections.length} past entries`}
          </button>
        )}
      </div>
    </div>
  );
}

// Compact Weekly Prize Card for side-by-side layout
function WeeklyPrizeCardCompact({ weeklyPrize, t }) {
  const prize = weeklyPrize.prize;
  const stats = weeklyPrize.week_stats || {};
  const earned = prize.earned;
  
  const lessonsNeeded = 5;
  const tasksNeeded = 15;
  const lessonsCompleted = Math.min(stats.lessons_completed || 0, lessonsNeeded);
  const tasksCompleted = Math.min(stats.tasks_completed || 0, tasksNeeded);
  const overallProgress = ((lessonsCompleted / lessonsNeeded) + (tasksCompleted / tasksNeeded)) / 2 * 100;

  return (
    <div className={`rounded-2xl overflow-hidden shadow-lg border-b-4 ${
      earned ? 'border-[#FFD166]' : 'border-[#9B5DE5]'
    }`}>
      {/* Header */}
      <div className="p-3 relative"
        style={{ 
          background: earned 
            ? "linear-gradient(135deg, #FFD166 0%, #F77F00 100%)" 
            : "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)"
        }}>
        <div className="flex items-center gap-2">
          <div className={`rounded-xl p-2 ${earned ? 'bg-white/30' : 'bg-white/20'}`}>
            <Gift size={20} color="white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/80 text-[10px] font-bold uppercase tracking-wider">
              {earned ? t('prize_earned') || "Earned!" : t('this_weeks_prize_title') || "This Week's Prize"}
            </p>
            <h3 className="font-black text-sm text-white truncate" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {prize.prize_name}
            </h3>
          </div>
          {earned && <Trophy size={18} color="white" fill="white" />}
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white p-3">
        {earned ? (
          <div className="text-center">
            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD166] to-[#F77F00] text-white font-black px-3 py-1.5 rounded-xl text-xs">
              <Trophy size={14} />
              {t('you_did_it') || 'You Did It!'}
            </span>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-gray-500">{t('progress') || 'Progress'}</span>
              <span className="text-xs font-black text-[#9B5DE5]">{Math.round(overallProgress)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ width: `${overallProgress}%`, background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
              />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
              <span>{lessonsCompleted}/{lessonsNeeded} lessons</span>
              <span>{tasksCompleted}/{tasksNeeded} tasks</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact Memory Verse Card for side-by-side layout
function MemoryVerseCardCompact({ memoryVerse, childId, onUpdate, t }) {
  const [practicing, setPracticing] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const isCompleted = memoryVerse.completed;
  const practiceCount = memoryVerse.practice_count || 0;

  const readVerseAloud = () => {
    if ('speechSynthesis' in window && memoryVerse?.verse) {
      window.speechSynthesis.cancel();
      const text = `${memoryVerse.verse}. ${memoryVerse.reference}`;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.85;
      utterance.pitch = 1.1;
      utterance.lang = 'en-US';
      utterance.onstart = () => setIsReading(true);
      utterance.onend = () => setIsReading(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const handlePractice = async () => {
    setPracticing(true);
    readVerseAloud();
    try {
      await axios.post(`${API}/memory-verse/weekly/${childId}/practice`);
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setPracticing(false); }
  };

  return (
    <div className={`rounded-2xl overflow-hidden shadow-lg border-b-4 ${
      isCompleted ? 'border-[#06D6A0]' : 'border-[#118AB2]'
    }`}>
      {/* Header */}
      <div className="p-3 relative"
        style={{ 
          background: isCompleted 
            ? "linear-gradient(135deg, #06D6A0 0%, #118AB2 100%)" 
            : "linear-gradient(135deg, #118AB2 0%, #073B4C 100%)"
        }}>
        <div className="flex items-center gap-2">
          <div className={`rounded-xl p-2 ${isCompleted ? 'bg-white/30' : 'bg-white/20'}`}>
            <BookMarked size={20} color="white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/80 text-[10px] font-bold uppercase tracking-wider">
              {isCompleted ? t('memorized') || "Memorized!" : t('memory_verse') || "Memory Verse"}
            </p>
            <h3 className="font-black text-sm text-white truncate" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {memoryVerse.theme}
            </h3>
          </div>
          {isCompleted && <Check size={18} color="white" strokeWidth={3} />}
        </div>
      </div>

      {/* Verse Content */}
      <div className="bg-white p-3">
        <p className="text-sm text-[#073B4C] leading-snug text-center italic mb-1 line-clamp-2"
          style={{ fontFamily: "Georgia, serif" }}>
          "{memoryVerse.verse}"
        </p>
        <p className="text-xs font-bold text-center mb-2"
          style={{ color: isCompleted ? "#06D6A0" : "#118AB2" }}>
          — {memoryVerse.reference}
        </p>

        {isCompleted ? (
          <div className="text-center">
            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#06D6A0] to-[#118AB2] text-white font-black px-3 py-1.5 rounded-xl text-xs">
              <Star size={14} fill="white" />
              +5 Stars!
            </span>
          </div>
        ) : (
          <div>
            <div className="flex justify-center gap-0.5 mb-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <div key={n}
                  className={`w-3 h-3 rounded-full flex items-center justify-center ${
                    n <= practiceCount ? 'bg-[#118AB2]' : 'bg-gray-200'
                  }`}>
                  {n <= practiceCount && <Check size={8} color="white" strokeWidth={3} />}
                </div>
              ))}
            </div>
            <button
              onClick={handlePractice}
              disabled={practicing || isReading}
              className="w-full py-2 rounded-xl font-bold text-xs text-white transition-all disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}
            >
              {isReading ? "🔊 Reading..." : practicing ? "..." : `${t('practice') || 'Practice'} (${practiceCount}/10)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Collapsible Parent Notes Section
function ParentNotesSection({ parentNotes, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="max-w-4xl mx-auto px-4 mt-4">
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div 
          className="p-3 cursor-pointer flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #EF476F, #FFD166)" }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <Heart size={18} color="white" fill="white" />
            <span className="font-black text-white text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('notes_from_parents') || 'Notes from Mom & Dad'}
            </span>
            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {parentNotes.length}
            </span>
          </div>
          <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
        
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
          <div className="p-3 space-y-2">
            {parentNotes.slice(0, 3).map((note, i) => (
              <div key={i} className="rounded-xl p-3 flex items-start gap-2"
                style={{ background: ["#FFF0F3", "#FFF9E6", "#F0F4FF"][i % 3] }}>
                <Heart size={14} color="#EF476F" fill="#EF476F" className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 text-sm leading-snug" style={{ fontFamily: "Quicksand, sans-serif" }}>
                    "{note.content}"
                  </p>
                  <p className="text-gray-400 text-xs mt-1">{note.parent_name || 'Parent'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
